import React, {useEffect, useState} from 'react';
import './PartnerFormPage.css';
import PageButton from '../../elements/PageButton/PageButton';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {API_BASE} from '../../../utils/apiConfig';
import {INVENTORY_ITEMS} from '../../../constants/inventoryItems';
import {MINUS_SIGN} from '../../../constants/text';
import {copyTextToClipboard} from '../../../utils/other/copyTextToClipboard';
import {
  getPartnerRewardDisplayProps,
  getPartnerRewardMediaUrls,
} from '../../../utils/partner/getPartnerRewardDisplayProps';
import FloatingBackButton from '../../elements/FloatingBackButton/FloatingBackButton';

const PAGE_ID = 'PartnerFormPage';

// Типы наград рефералам — любой предмет инвентаря, кроме «всех фракций»
export const PARTNER_REWARD_TYPE_OPTIONS = [
  {value: INVENTORY_ITEMS.NUMENS, label: 'Нумены'},
  {value: INVENTORY_ITEMS.CRYSTALS, label: 'Кристаллы'},
  {value: INVENTORY_ITEMS.PREMIUM, label: 'Премиум (дни)'},
  {value: INVENTORY_ITEMS.CHEST, label: 'Сундук'},
  {value: INVENTORY_ITEMS.FACTION, label: 'Фракция'},
  {value: INVENTORY_ITEMS.FACTION_SKIN, label: 'Образ фракции'},
  {value: INVENTORY_ITEMS.FACTION_UNIT_SKIN, label: 'Образ отрядов фракции'},
];

const NUMERIC_REWARD_TYPES = [INVENTORY_ITEMS.NUMENS, INVENTORY_ITEMS.CRYSTALS, INVENTORY_ITEMS.PREMIUM];

const REWARD_VALUE_PLACEHOLDERS = {
  [INVENTORY_ITEMS.NUMENS]: 'Количество нуменов',
  [INVENTORY_ITEMS.CRYSTALS]: 'Количество кристаллов',
  [INVENTORY_ITEMS.PREMIUM]: 'Количество дней',
  [INVENTORY_ITEMS.CHEST]: 'id сундука',
  [INVENTORY_ITEMS.FACTION]: 'id фракции',
  [INVENTORY_ITEMS.FACTION_SKIN]: 'id скина',
  [INVENTORY_ITEMS.FACTION_UNIT_SKIN]: 'id фракции',
};

function formatDate(isoString) {
  if (!isoString) return '—';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })}, ${date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'})}`;
}

/**
 * Страница данных партнера. Три режима:
 *  - админ, пользователь еще не партнер («Назначить партнером»): пустая форма,
 *    идентификатор предзаполнен никнеймом;
 *  - админ, партнер уже назначен («Инфо о партнере»): та же форма с текущими
 *    значениями, статусные поля, «Приостановить партнерство» и история
 *    регистраций;
 *  - сам партнер (кнопка «Партнерка» в меню): только просмотр (isSelfView).
 */
function PartnerFormPage({targetUserId, targetUserName, isSelfView = false, onBack, onRegistrationsClick}) {
  usePageAnalytics('PartnerFormPage');

  const readOnly = isSelfView;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [partner, setPartner] = useState(null);

  const [resourceUrls, setResourceUrls] = useState(['']);
  const [donationPercent, setDonationPercent] = useState('');
  const [rewards, setRewards] = useState([{itemType: INVENTORY_ITEMS.NUMENS, value: ''}]);
  const [referalId, setReferalId] = useState('');

  const [saving, setSaving] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [formError, setFormError] = useState(null);
  const [savedMessage, setSavedMessage] = useState(null);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [isDownloadingMedia, setIsDownloadingMedia] = useState(false);

  // «Скачать медиа» (у партнера, под списком наград): скачивает картинки всех
  // установленных наград — партнеру для оформления анонса реферальной ссылки
  // на своих ресурсах. Скачивания идут с паузой: серию мгновенных кликов по
  // временным <a download> браузер может частично проглотить
  const handleDownloadMedia = async () => {
    if (isDownloadingMedia) return;
    const mediaUrls = [...new Set(rewards.flatMap((reward) => getPartnerRewardMediaUrls(reward)))];
    if (!mediaUrls.length) return;

    setIsDownloadingMedia(true);
    try {
      for (const url of mediaUrls) {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = url.split('/').pop();
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    } finally {
      setIsDownloadingMedia(false);
    }
  };

  // Заполнение формы данными партнера (после загрузки/сохранения)
  const applyPartner = (partnerData) => {
    setPartner(partnerData);
    setResourceUrls(partnerData.partnerResourceUrls?.length ? partnerData.partnerResourceUrls : ['']);
    setDonationPercent(partnerData.partnerDonationPercent != null ? String(partnerData.partnerDonationPercent) : '');
    setRewards(partnerData.partnerReferalRewards?.length
      ? partnerData.partnerReferalRewards.map((reward) => ({itemType: reward.itemType, value: String(reward.value)}))
      : [{itemType: INVENTORY_ITEMS.NUMENS, value: ''}]);
    setReferalId(partnerData.partnerReferalId || '');
  };

  useEffect(() => {
    const fetchPartner = async () => {
      try {
        const url = isSelfView
          ? `${API_BASE}/partner/my-info`
          : `${API_BASE}/admin/partner-info/${targetUserId}`;
        const res = await fetch(url, {credentials: 'include'});
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Не удалось загрузить данные партнера');
        }
        if (data.partner.partnerActivatedAt) {
          applyPartner(data.partner);
        } else {
          // Новый партнер: идентификатор по умолчанию — никнейм пользователя
          setReferalId(targetUserName || '');
        }
      } catch (e) {
        console.error('Partner info error', e);
        setLoadError(e.message || 'Не удалось загрузить данные партнера');
      } finally {
        setLoading(false);
      }
    };
    fetchPartner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId, isSelfView]);

  const validateForm = () => {
    if (resourceUrls.some((url) => !url.trim())) {
      return 'Заполните все ссылки на ресурсы партнера';
    }
    const percent = parseInt(donationPercent, 10);
    if (!Number.isInteger(percent) || percent < 0 || percent > 100 || String(percent) !== donationPercent.trim()) {
      return 'Процент с донатов должен быть целым числом от 0 до 100';
    }
    for (const reward of rewards) {
      if (!reward.itemType) {
        return 'Выберите тип каждой награды';
      }
      if (!String(reward.value).trim()) {
        return 'Заполните значения всех наград';
      }
      if (NUMERIC_REWARD_TYPES.includes(reward.itemType)) {
        const amount = parseInt(reward.value, 10);
        if (!Number.isInteger(amount) || amount <= 0) {
          return 'Количество награды должно быть целым положительным числом';
        }
      }
    }
    if (!referalId.trim()) {
      return 'Укажите идентификатор партнера';
    }
    return null;
  };

  const handleSave = async () => {
    if (saving) return;
    setSavedMessage(null);
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError(null);

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/admin/save-partner`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          targetUserId,
          partnerResourceUrls: resourceUrls.map((url) => url.trim()),
          partnerDonationPercent: parseInt(donationPercent, 10),
          partnerReferalRewards: rewards.map((reward) => ({
            itemType: reward.itemType,
            value: String(reward.value).trim(),
          })),
          partnerReferalId: referalId.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Не удалось сохранить данные партнера');
      }
      applyPartner(data.partner);
      setSavedMessage('Данные партнера сохранены');
    } catch (e) {
      console.error('Save partner error', e);
      setFormError(e.message || 'Не удалось сохранить данные партнера');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async () => {
    if (suspending) return;
    setSuspending(true);
    setSavedMessage(null);
    setFormError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/suspend-partner`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({targetUserId}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Не удалось приостановить партнерство');
      }
      applyPartner(data.partner);
      setSavedMessage('Партнерство приостановлено');
    } catch (e) {
      console.error('Suspend partner error', e);
      setFormError(e.message || 'Не удалось приостановить партнерство');
    } finally {
      setSuspending(false);
    }
  };

  // Возобновление приостановленного партнерства: флаг активности снова true,
  // дата активации обновляется на момент возобновления
  const handleResume = async () => {
    if (resuming) return;
    setResuming(true);
    setSavedMessage(null);
    setFormError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/resume-partner`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({targetUserId}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Не удалось возобновить партнерство');
      }
      applyPartner(data.partner);
      setSavedMessage('Партнерство возобновлено');
    } catch (e) {
      console.error('Resume partner error', e);
      setFormError(e.message || 'Не удалось возобновить партнерство');
    } finally {
      setResuming(false);
    }
  };

  const isActivated = Boolean(partner?.partnerActivatedAt);
  const title = isSelfView ? 'Партнерка' : (isActivated ? 'Инфо о партнере' : 'Назначение партнером');

  return (
    <>
      <FloatingBackButton onBack={onBack} pageId={PAGE_ID}/>

      <h1 className="lobby-page-title partner-form-page-title">
        {title}
        {targetUserName && <span className="partner-form-page-subtitle">{targetUserName}</span>}
      </h1>

      {loading && <p className="partner-form-status">Загрузка...</p>}
      {loadError && <p className="partner-form-status partner-form-status_error">{loadError}</p>}

      {!loading && !loadError && (
        <div className="partner-form">
          {isActivated && (
            <div className="partner-form-status-card">
              <div className="partner-form-status-row">
                <span className="partner-form-status-label">Партнер активен</span>
                <span
                  className={`partner-form-status-value ${partner.isActivePartner ? 'text-success' : 'text-rating'}`}
                >
                  {partner.isActivePartner ? 'Да' : 'Нет'}
                </span>
              </div>
              <div className="partner-form-status-row">
                <span className="partner-form-status-label">Дата активации</span>
                <span className="partner-form-status-value">{formatDate(partner.partnerActivatedAt)}</span>
              </div>
              {!partner.isActivePartner && (
                <div className="partner-form-status-row">
                  <span className="partner-form-status-label">Дата деактивации</span>
                  <span className="partner-form-status-value">{formatDate(partner.partnerDeactivatedAt)}</span>
                </div>
              )}
            </div>
          )}

          <label className="partner-form-label">Ресурсы партнера (ссылки)</label>
          {resourceUrls.map((url, index) => {
            const isLast = index === resourceUrls.length - 1;
            return (
              <div key={index} className="partner-form-row">
                <input
                  className="partner-form-input"
                  type="text"
                  value={url}
                  onChange={(e) => {
                    const next = [...resourceUrls];
                    next[index] = e.target.value;
                    setResourceUrls(next);
                  }}
                  placeholder="https://..."
                  disabled={readOnly || saving}
                />
                {!readOnly && (
                  isLast ? (
                    <button
                      type="button"
                      className="partner-form-row-button"
                      onClick={() => setResourceUrls([...resourceUrls, ''])}
                      title="Добавить ресурс"
                    >
                      +
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="partner-form-row-button"
                      onClick={() => setResourceUrls(resourceUrls.filter((_, i) => i !== index))}
                      title="Удалить ресурс"
                    >
                      {MINUS_SIGN}
                    </button>
                  )
                )}
              </div>
            );
          })}

          <label className="partner-form-label" htmlFor="partner-donation-percent">
            Процент с донатов приведенных игроков
          </label>
          <input
            id="partner-donation-percent"
            className="partner-form-input"
            type="number"
            min="0"
            max="100"
            value={donationPercent}
            onChange={(e) => setDonationPercent(e.target.value)}
            placeholder="Например, 10"
            disabled={readOnly || saving}
          />

          <label className="partner-form-label">Награды рефералам за регистрацию</label>
          {rewards.map((reward, index) => {
            const isLast = index === rewards.length - 1;
            const isNumeric = NUMERIC_REWARD_TYPES.includes(reward.itemType);
            // Визуализация награды: иконка + русское название из конфигурации
            // предмета. У самого партнера (readOnly) поля ввода скрыты —
            // остается только она
            const hasValue = Boolean(String(reward.value).trim());
            const display = hasValue ? getPartnerRewardDisplayProps(reward) : null;
            return (
              <div key={index} className="partner-form-reward">
                {!readOnly && (
                  <div className="partner-form-row">
                    <select
                      className="partner-form-input partner-form-select"
                      value={reward.itemType}
                      onChange={(e) => {
                        const next = [...rewards];
                        next[index] = {...next[index], itemType: e.target.value, value: ''};
                        setRewards(next);
                      }}
                      disabled={saving}
                    >
                      {PARTNER_REWARD_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <input
                      className="partner-form-input partner-form-reward-value"
                      type={isNumeric ? 'number' : 'text'}
                      min={isNumeric ? '1' : undefined}
                      value={reward.value}
                      onChange={(e) => {
                        const next = [...rewards];
                        next[index] = {...next[index], value: e.target.value};
                        setRewards(next);
                      }}
                      placeholder={REWARD_VALUE_PLACEHOLDERS[reward.itemType] || 'Значение'}
                      disabled={saving}
                    />
                    {isLast ? (
                      <button
                        type="button"
                        className="partner-form-row-button"
                        onClick={() => setRewards([...rewards, {itemType: INVENTORY_ITEMS.NUMENS, value: ''}])}
                        title="Добавить награду"
                      >
                        +
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="partner-form-row-button"
                        onClick={() => setRewards(rewards.filter((_, i) => i !== index))}
                        title="Удалить награду"
                      >
                        {MINUS_SIGN}
                      </button>
                    )}
                  </div>
                )}
                {display && (
                  <div
                    className={`partner-form-reward-preview ${display.isKnown ? '' : 'partner-form-reward-preview_unknown'} partner-form-reward-preview_type-${display.itemType}`}
                  >
                    {display.iconSrc && (
                      <img className="partner-form-reward-preview-icon" src={display.iconSrc} alt=""/>
                    )}
                    <span className={`partner-form-reward-preview-title ${display.colorClass}`}>
                      {display.title}
                      {display.amountLabel ? `: ${display.amountLabel}` : ''}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
          {readOnly && rewards.some((reward) => getPartnerRewardMediaUrls(reward).length > 0) && (
            <button
              type="button"
              className="partner-form-download-media"
              onClick={handleDownloadMedia}
              disabled={isDownloadingMedia}
              title="Скачать картинки всех наград для оформления анонсов"
            >
              {isDownloadingMedia ? 'Скачивание...' : 'Скачать медиа'}
            </button>
          )}

          <label className="partner-form-label" htmlFor="partner-referal-id">
            Идентификатор партнера (часть реферальной ссылки)
          </label>
          <input
            id="partner-referal-id"
            className="partner-form-input"
            type="text"
            value={referalId}
            onChange={(e) => setReferalId(e.target.value)}
            placeholder="Например, никнейм"
            disabled={readOnly || saving}
          />
          {referalId.trim() && (() => {
            // Ссылка показывается в том же виде, в каком идентификатор ляжет в
            // базу — в нижнем регистре (сервер нормализует его при сохранении,
            // а поиск партнера по ссылке регистронезависим)
            const referalUrl = `${window.location.origin}/referal/partner/${encodeURIComponent(referalId.trim().toLowerCase())}`;
            const handleCopyLink = async () => {
              try {
                // Утилита с фолбэком на execCommand: на мобильных по http
                // (дев-сборка по IP) navigator.clipboard недоступен вовсе
                await copyTextToClipboard(referalUrl);
                setIsLinkCopied(true);
                setTimeout(() => setIsLinkCopied(false), 2000);
              } catch (e) {
                console.error('Failed to copy partner referal url', e);
                // Последняя страховка: показать ссылку в prompt — из него
                // можно скопировать вручную на любом устройстве
                window.prompt('Не удалось скопировать автоматически. Скопируйте ссылку вручную:', referalUrl);
              }
            };
            return (
              <div className="partner-form-referal-url-row">
                <button
                  type="button"
                  className="partner-form-referal-url"
                  onClick={handleCopyLink}
                  title="Нажмите, чтобы скопировать ссылку"
                >
                  {referalUrl}
                </button>
                {isLinkCopied && <span className="partner-form-referal-url-copied">Скопировано!</span>}
              </div>
            );
          })()}

          {formError && <p className="partner-form-status partner-form-status_error">{formError}</p>}
          {savedMessage && <p className="partner-form-status partner-form-status_success">{savedMessage}</p>}
        </div>
      )}

      <div className="main-menu-buttons partner-form-page-buttons">
        {!loading && !loadError && !readOnly && (
          <PageButton
            onClick={handleSave}
            pageId={PAGE_ID}
            buttonId="save"
            disabled={saving}
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </PageButton>
        )}
        {!loading && !loadError && !readOnly && isActivated && partner.isActivePartner && (
          <PageButton
            className="main-menu-button_secondary"
            onClick={handleSuspend}
            pageId={PAGE_ID}
            buttonId="suspend"
            disabled={suspending}
          >
            {suspending ? 'Приостановка...' : 'Приостановить партнерство'}
          </PageButton>
        )}
        {!loading && !loadError && !readOnly && isActivated && !partner.isActivePartner && (
          <PageButton
            className="main-menu-button_secondary"
            onClick={handleResume}
            pageId={PAGE_ID}
            buttonId="resume"
            disabled={resuming}
          >
            {resuming ? 'Возобновление...' : 'Возобновить партнерство'}
          </PageButton>
        )}
        {!loading && !loadError && isActivated && onRegistrationsClick && (
          <PageButton
            className="main-menu-button_secondary"
            onClick={onRegistrationsClick}
            pageId={PAGE_ID}
            buttonId="registrations"
          >
            Посмотреть историю регистраций
          </PageButton>
        )}
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId="back">
          Назад
        </PageButton>
      </div>
    </>
  );
}

export default PartnerFormPage;
